import React, { useEffect, useRef, useState } from 'react';
import '../styles/MicTest.css';

/**
 * MicTest Component
 * Real-time microphone volume visualization (Ammeter)
 * Detects rhythmic peaks (e.g., 'BA', 'BU' syllables) specifically for human voice.
 *
 * Calibrazione automatica del rumore di fondo:
 *  - I primi secondi (mentre il bambino ascolta l'istruzione e sta in silenzio)
 *    vengono usati per misurare il rumore ambientale della stanza/microfono.
 *  - Il segnale viene poi "ripulito" sottraendo quel rumore di fondo, e le sillabe
 *    vengono rilevate con una soglia RELATIVA al fondo: così chi parla piano in una
 *    stanza silenziosa viene riconosciuto comunque, senza penalizzazioni.
 *
 * Riconoscimento della SOLA voce umana (no porte, tosse, tasti, ...):
 *  - La voce sonora (le vocali di BA/BU) è PERIODICA: ha un'altezza (pitch) con
 *    armoniche regolari. Tosse, click di tastiera e porte sono transitori RUMOROSI
 *    e aperiodici. Misuriamo la periodicità via autocorrelazione e contiamo una
 *    sillaba solo se: (1) c'è un pitch chiaro nel range della voce umana,
 *    (2) il suono è SOSTENUTO per un minimo di tempo (un impulso secco non basta),
 *    (3) l'energia supera il rumore di fondo.
 */
export default function MicTest({ onComplete }) {
    const [volume, setVolume] = useState(0);
    const [peaksCount, setPeaksCount] = useState(0);
    const [isReady, setIsReady] = useState(false);
    const [phase, setPhase] = useState('calibrating'); // 'calibrating' | 'listening'

    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const requestRef = useRef(null);

    // Calibrazione rumore di fondo
    const calibrationStartRef = useRef(null);
    const calibrationSumRef = useRef(0);
    const calibrationCountRef = useRef(0);
    const calibratedRef = useRef(false);
    const noiseFloorRef = useRef(0);

    // Tracciamento del segmento vocale (per contare le sillabe sostenute)
    const voicedStartRef = useRef(null);
    const lastVoicedRef = useRef(0);
    const segmentCountedRef = useRef(false);

    // Configurazione specifica per voce umana
    const MIN_FREQUENCY = 100; // Hz
    const MAX_FREQUENCY = 3000; // Hz
    const CALIBRATION_DURATION = 2500; // ms iniziali per misurare il rumore di fondo
    const VOICE_MARGIN = 12; // quanto la voce deve superare il rumore di fondo (segnale ripulito)

    // Discriminazione voce umana vs altri suoni (porte, tosse, tastiera, ...)
    const F0_MIN = 80;  // Hz - frequenza fondamentale minima (voce adulta grave)
    const F0_MAX = 550; // Hz - frequenza fondamentale massima (voce di bambino)
    const CLARITY_THRESHOLD = 0.7; // periodicità minima per considerarlo "voce sonora"
    const MIN_VOICED_MS = 70;  // durata minima del suono vocale per contare una sillaba
    const RELEASE_MS = 120;    // pausa di silenzio/non-voce che separa due sillabe distinte

    useEffect(() => {
        let stream = null;

        async function setupMic() {
            try {
                // Stessa cattura grezza usata per le registrazioni degli esercizi
                // (niente noise suppression / AGC / echo cancellation): così la
                // calibrazione del rumore di fondo riflette le condizioni reali.
                stream = await navigator.mediaDevices.getUserMedia({
                    audio: {
                        echoCancellation: false,
                        noiseSuppression: false,
                        autoGainControl: false,
                        channelCount: 1,
                    },
                });
                audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
                analyserRef.current = audioContextRef.current.createAnalyser();
                const source = audioContextRef.current.createMediaStreamSource(stream);
                source.connect(analyserRef.current);

                // fftSize più grande: finestra temporale sufficiente per misurare il pitch
                // (~43ms @48kHz, copre più periodi anche delle voci gravi)
                analyserRef.current.fftSize = 2048;

                const sampleRate = audioContextRef.current.sampleRate;
                const bufferLength = analyserRef.current.frequencyBinCount;
                const dataArray = new Uint8Array(bufferLength);
                const timeData = new Float32Array(analyserRef.current.fftSize);

                // Calcoliamo gli indici dei bin per il range della voce umana
                const minBin = Math.floor(MIN_FREQUENCY / (sampleRate / analyserRef.current.fftSize));
                const maxBin = Math.floor(MAX_FREQUENCY / (sampleRate / analyserRef.current.fftSize));

                // Lag (in campioni) corrispondenti al range di pitch della voce
                const minLag = Math.max(1, Math.floor(sampleRate / F0_MAX));
                const maxLag = Math.floor(sampleRate / F0_MIN);

                /**
                 * Ritorna true se la finestra audio corrente contiene voce SONORA:
                 * un suono periodico (con pitch chiaro) nel range della voce umana.
                 * Usa l'autocorrelazione del segnale nel dominio del tempo.
                 */
                const isVoiced = () => {
                    analyserRef.current.getFloatTimeDomainData(timeData);
                    const size = timeData.length;
                    const top = Math.min(maxLag, size - 1);

                    // 1) Troviamo il lag con la massima autocorrelazione (candidato periodo)
                    let bestLag = -1;
                    let bestRaw = -Infinity;
                    for (let lag = minLag; lag <= top; lag++) {
                        let s = 0;
                        for (let i = 0; i < size - lag; i++) {
                            s += timeData[i] * timeData[i + lag];
                        }
                        if (s > bestRaw) {
                            bestRaw = s;
                            bestLag = lag;
                        }
                    }
                    if (bestLag < 0) return false;

                    // 2) Correlazione normalizzata al lag migliore = "chiarezza" del pitch (0..1)
                    let num = 0, d1 = 0, d2 = 0;
                    for (let i = 0; i < size - bestLag; i++) {
                        const a = timeData[i];
                        const b = timeData[i + bestLag];
                        num += a * b;
                        d1 += a * a;
                        d2 += b * b;
                    }
                    const clarity = (d1 > 0 && d2 > 0) ? num / Math.sqrt(d1 * d2) : 0;
                    const pitch = sampleRate / bestLag;

                    return clarity >= CLARITY_THRESHOLD && pitch >= F0_MIN && pitch <= F0_MAX;
                };

                const update = () => {
                    analyserRef.current.getByteFrequencyData(dataArray);

                    // Calcoliamo l'energia solo nel range della voce
                    let voiceSum = 0;
                    for (let i = minBin; i <= maxBin; i++) {
                        voiceSum += dataArray[i];
                    }
                    const voiceAverage = voiceSum / (maxBin - minBin + 1);

                    const now = Date.now();
                    if (calibrationStartRef.current === null) calibrationStartRef.current = now;
                    const elapsed = now - calibrationStartRef.current;

                    if (elapsed < CALIBRATION_DURATION) {
                        // --- Fase di calibrazione: misuriamo il rumore di fondo ---
                        calibrationSumRef.current += voiceAverage;
                        calibrationCountRef.current += 1;
                        // L'ago resta a riposo: stiamo solo "ascoltando la stanza"
                        setVolume(prev => prev * 0.6);
                    } else {
                        // Alla prima iterazione dopo la calibrazione, fissiamo il rumore di fondo
                        if (!calibratedRef.current) {
                            noiseFloorRef.current = calibrationCountRef.current > 0
                                ? calibrationSumRef.current / calibrationCountRef.current
                                : 0;
                            calibratedRef.current = true;
                            setPhase('listening');
                        }

                        // --- Fase di ascolto: segnale ripulito dal rumore di fondo ---
                        const cleanSignal = Math.max(0, voiceAverage - noiseFloorRef.current);

                        // Aggiorniamo il volume per l'ago (un po' di smoothing)
                        setVolume(prev => prev * 0.4 + cleanSignal * 0.6);

                        // Soglia RELATIVA: un margine minimo, che cresce un po' se la stanza è rumorosa.
                        // Così chi parla piano in una stanza silenziosa viene rilevato comunque.
                        const dynamicMargin = Math.max(VOICE_MARGIN, noiseFloorRef.current * 0.4);
                        const loud = cleanSignal > dynamicMargin;

                        // Rilevamento sillaba ('BA', 'BU'): SOLO voce umana sostenuta.
                        // Tosse / porte / tasti sono forti ma non periodici e/o non sostenuti.
                        if (loud && isVoiced()) {
                            lastVoicedRef.current = now;
                            if (voicedStartRef.current === null) {
                                voicedStartRef.current = now;
                                segmentCountedRef.current = false;
                            }
                            const voicedDuration = now - voicedStartRef.current;

                            if (!segmentCountedRef.current && voicedDuration >= MIN_VOICED_MS) {
                                segmentCountedRef.current = true; // un solo conteggio per segmento vocale
                                setPeaksCount(prev => {
                                    const newCount = prev + 1;
                                    if (newCount >= 4) setIsReady(true);
                                    return newCount;
                                });

                                // Feedback visivo immediato per il picco vocale
                                setVolume(110); // Spinge l'ago verso l'alto
                            }
                        } else {
                            // Fine del segmento vocale solo dopo una pausa: tollera micro-cali di pitch
                            if (now - lastVoicedRef.current > RELEASE_MS) {
                                voicedStartRef.current = null;
                            }
                        }
                    }

                    requestRef.current = requestAnimationFrame(update);
                };

                update();
            } catch (err) {
                console.error("Errore accesso microfono:", err);
            }
        }

        setupMic();

        return () => {
            if (requestRef.current) cancelAnimationFrame(requestRef.current);
            if (audioContextRef.current) audioContextRef.current.close();
            if (stream) stream.getTracks().forEach(track => track.stop());
        };
    }, []);

    // Ago dell'amperometro: rotazione da -90deg a 90deg (range 0-128)
    const needleRotation = (Math.min(volume, 128) / 128) * 180 - 90;

    // Calcolo per l'arco verde (zona "ti sento bene")
    const radius = 158;
    const circumference = Math.PI * radius;
    const thresholdPercent = (VOICE_MARGIN / 128);
    const dashArray = `${circumference * (1 - thresholdPercent)} ${circumference}`;
    const dashOffset = 0;

    return (
        <div className="mic-test-container">
            <h2 className="mic-test-title">Prova Microfono</h2>

            <div className="ammeter-wrapper">



                {/* Arco Target Verde */}
                <div className="ammeter-target-arc">
                    <svg className="target-arc-svg" viewBox="0 0 320 160">
                        <path
                            d="M 318,160 A 158,158 0 0,0 2,160"
                            fill="none"
                            stroke="#66BB6A"
                            strokeWidth="8"
                            strokeDasharray={dashArray}
                            strokeDashoffset={dashOffset}
                            style={{
                                opacity: 0.6,
                                filter: 'drop-shadow(0 0 5px #66BB6A)'
                            }}
                        />
                    </svg>
                </div>

                <div className="ammeter-needle" style={{
                    transform: `translateX(-50%) rotate(${needleRotation}deg)`
                }} />
                <div className="ammeter-center" />
            </div>

            {phase === 'calibrating' ? (
                <div className="mic-status-container">
                    <span className="mic-status-text">🎧 Un attimo, sto ascoltando la stanza…</span>
                </div>
            ) : (
                <div className="mic-status-container">
                    <span className="mic-status-text">Sillabe rilevate:</span>
                    <div className="peaks-indicator">
                        {[...Array(4)].map((_, i) => (
                            <div
                                key={i}
                                className={`peak-dot ${i < peaksCount ? 'active' : ''}`}
                            />
                        ))}
                    </div>
                </div>
            )}

            <button
                onClick={onComplete}
                disabled={!isReady}
                className="mic-test-button"
            >
                {phase === 'calibrating' ? 'Attendi…' : (isReady ? 'Avanti →' : 'Parla...')}
            </button>
        </div>
    );
}
